import { ProductCardType } from '@/constants'
import { useNotification } from '@/containers'
import type { FlashSaleTab, FlashSaleTime, Product } from '@/types'
import { type FlashSale as FlashSaleType } from '@/types'
import { getTranslate } from '@/utils/api-interceptors'
import { getFlashSaleTime, getFlashSaleTimeDetail } from '@/utils/endpoint'
import dayjs from 'dayjs'
import { sortBy } from 'lodash'
import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'
import Skeleton from 'react-loading-skeleton'
import { Autoplay, FreeMode, Grid, Navigation } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import { v4 as uuidv4 } from 'uuid'
import ProductCard from '../ProductCard'
import CountdownFlashSale from '../ProductCard/CountdownFlashSale'
import ProductCardSkeleton from '../Skeleton/ProductCardSkeleton'
const FlashSale = () => {
  const { systemConfig } = useNotification()
  const [flashSale, setFlashSale] = useState<FlashSaleType | null>(null)
  const [flashSaleTime, setFlashSaleTime] = useState<FlashSaleTime[] | null>(
    null
  )
  const [loading, setLoading] = useState(true)
  // '' = "Tất cả"; còn lại là `<loại tab>:<khoá>` (cat:<id danh mục> hoặc group:<id nhóm>)
  const [tabActive, setTabActive] = useState('')
  const [tabs, setTabs] = useState<FlashSaleTab[] | null>(null)
  const [flashSaleTimeActive, setFlashSaleTimeActive] =
    useState<FlashSaleTime | null>(null)

  useEffect(() => {
    // Giữ skeleton tới khi có chi tiết flash sale (effect bên dưới). Trước
    // đây tắt loading ngay khi có time-lines -> khối co về 0 rồi mới giãn ra
    // khi chi tiết về, đẩy toàn bộ nội dung bên dưới lên/xuống (CLS).
    getFlashSaleTime().then((res: FlashSaleTime[] | null) => {
      if (res) {
        const fTime = res.filter(item => item.fsStatus !== 'ended')
        if (fTime.length === 0) setLoading(false)
        return setFlashSaleTime(fTime)
      }
      setLoading(false)
    })
  }, [])

  useEffect(() => {
    if (flashSaleTime) {
      setFlashSaleTimeActive(flashSaleTime[0])
    }
  }, [flashSaleTime])

  // Bấm nhanh hai tab thì phản hồi của lần gọi trước có thể về sau: chỉ nhận
  // phản hồi của lần gọi mới nhất, nếu không tab đang sáng hiện nhầm danh sách.
  const detailRequestId = useRef(0)
  const lastDetailRequest = useRef('')
  const loadFlashSaleDetail = (timeId: string, tabKey: string) => {
    // Đổi khung giờ khi đang ở một tab thì cả hai effect bên dưới cùng gọi
    // "Tất cả" của khung giờ mới: chỉ gọi một lần.
    const signature = `${timeId}|${tabKey}`
    if (signature === lastDetailRequest.current) return
    lastDetailRequest.current = signature

    const requestId = ++detailRequestId.current
    const [type, key] = tabKey.split(':')
    getFlashSaleTimeDetail(
      timeId,
      type === 'cat' ? key : '',
      type === 'group' ? key : ''
    ).then(res => {
      if (requestId !== detailRequestId.current) return
      setLoading(false)
      if (!res) return
      setFlashSale(toSort(res))
      // Lần gọi theo tab danh mục lẻ không trả `tabs`: giữ dãy tab đang có.
      if (res.tabs || !tabKey) setTabs(res.tabs || null)
      // Nhóm không còn (admin vừa đổi, hoặc backend đang chạy route gốc và đã
      // trả toàn bộ sản phẩm): đưa tab đang sáng về "Tất cả" cho khớp dữ liệu.
      if (
        type === 'group' &&
        !res.tabs?.some(tab => tab.type === 'group' && tab.key === key)
      ) {
        setTabActive('')
      }
    })
  }

  useEffect(() => {
    if (flashSaleTimeActive) {
      loadFlashSaleDetail(flashSaleTimeActive._id, tabActive)
    }
  }, [tabActive])

  useEffect(() => {
    if (flashSaleTimeActive) {
      // Khoá nhóm của flash sale này không thuộc flash sale kia: khung giờ mới
      // luôn bắt đầu từ "Tất cả".
      loadFlashSaleDetail(flashSaleTimeActive._id, '')
      setTabActive('')
    }
  }, [flashSaleTimeActive])

  // Backend chưa trả `tabs` (bản cũ) thì mỗi danh mục là một tab như trước.
  const categoryTabs: FlashSaleTab[] =
    tabs ||
    (flashSale?.cats || []).map(
      (cat): FlashSaleTab => ({
        type: 'cat',
        key: cat._id,
        name: cat.name,
        catIds: [cat._id],
      })
    )

  const qtt =
    systemConfig?.imageQlt && systemConfig?.imageQlt != ''
      ? systemConfig?.imageQlt
      : 70
  const customStyle = {
    backgroundColor:
      flashSale?.customization?.blockBackgroundColor || '#ff64e90d',
    backgroundImage:
      flashSale?.customization?.blockBackgroundImage &&
      `url(${flashSale?.customization?.blockBackgroundImage}?format=webp&quality=${qtt})`,
    backgroundRepeat:
      flashSale?.customization?.blockBackgroundImage && 'no-repeat',
    backgroundPosition:
      flashSale?.customization?.blockBackgroundImage && 'center center',
    backgroundSize:
      flashSale?.customization?.blockBackgroundImage && '100% 100%',
  }

  const toSort = (data: FlashSaleType) => {
    if (data.products && data.products.length > 0) {
      const arrFlashSale: Product[] =
        data.products.map((e, i) => {
          return {
            ...e,
            index: i + 1,
            orderFs:
              e.flashSale && e.flashSale?.soldQuantity >= e.flashSale?.quantity
                ? 1
                : 0,
          }
        }) || []
      return { ...data, products: sortBy(arrFlashSale, 'orderFs', 'order') }
    }
    return data
  }

  return (
    <section className='w-full mb-3'>
      {/* Skeleton cao đúng bằng khối flash sale thật (918px, đo ở 360/390/412px)
          để khi dữ liệu về không đẩy nội dung bên dưới (CLS). */}
      {loading ? (
        <div className='rounded-sm py-3 px-2 h-[918px] bg-white-500 overflow-hidden'>
          <div className='flash-sale--header mb-2 relative min-h-[40px] z-10'>
            <Skeleton duration={0.9} height={24} width={150} />
            <Skeleton duration={0.9} height={18} width={170} />
            <span className='absolute right-[4px] top-1'>
              <Skeleton duration={0.9} height={30} width={100} />
            </span>
          </div>
          <div className='flash-sale--content z-10'>
            <div className='products grid grid-fs gap-3'>
              {[...new Array(6)].map(() => (
                <ProductCardSkeleton key={uuidv4()} />
              ))}
            </div>
          </div>
        </div>
      ) : (
        ''
      )}

      {flashSale ? (
        <div className={`${flashSale ? 'py-0' : ''}`} style={customStyle}>
          <div className='flash-sale--header mb-20 pl-3 relative min-h-[40px]'>
            {!flashSale?.customization?.blockBackgroundImage ? (
              <>
                <h2
                  className=' block w-full text-headingMedium font-bold'
                  style={{
                    color: flashSale?.customization?.titleColor,
                  }}
                >
                  {getTranslate(flashSale?.name)}
                </h2>
                <p
                  className='subTitle text-mainBody text-black-400'
                  style={{
                    color:
                      flashSale?.customization?.descriptionColor || '#000000',
                  }}
                >
                  {getTranslate(flashSale?.description)}
                </p>
              </>
            ) : (
              ''
            )}
          </div>

          <div className='flash-sale-time-countdown block py-2 ml-2 bg-[#F3F4F6] rounded-tl-xl rounded-bl-xl'>
            <Swiper
              modules={[Grid, FreeMode, Autoplay]}
              className='flashSaleSwp'
              slidesPerView={2.32}
              grid={{
                rows: 1,
                fill: 'row',
              }}
              slidesPerGroup={2}
              freeMode={true}
              spaceBetween={8}
            >
              {flashSaleTime
                ? flashSaleTime.map((item, i) => (
                    <SwiperSlide key={`item-${i}`}>
                      {item.fsStatus === 'running' ? (
                        <div
                          className={` font-bold rounded-xl py-2 h-[60px] w-full ${
                            flashSaleTimeActive?._id === item._id
                              ? 'bg-redPico text-white'
                              : 'bg-white text-[#000000]'
                          }`}
                          onClick={() => setFlashSaleTimeActive(item)}
                        >
                          <div className='text-[14px] text-center truncate px-1'>
                            {getTranslate(item.countdownLabel)?.trim() ||
                              'Chỉ còn'}
                          </div>
                          <div className='text-[20px] leading-6 flex justify-center items-center gap-1'>
                            <CountdownFlashSale
                              endTime={item.todayEndTime}
                              className={`flex gap-1 ${
                                flashSaleTimeActive?._id === item._id
                                  ? ''
                                  : 'text-redPico'
                              }`}
                            />
                          </div>
                        </div>
                      ) : (
                        <div
                          className={`rounded-xl py-2 h-[60px] w-full ${
                            flashSaleTimeActive?._id === item._id
                              ? 'bg-[#F00] text-white'
                              : 'bg-white text-[#000000]'
                          }`}
                          onClick={() => setFlashSaleTimeActive(item)}
                        >
                          <div className='text-[16px] leading-6 font-bold flex  justify-center items-center'>
                            <span>
                              {dayjs(item.todayStartTime).format('HH')}
                            </span>
                            <span>:</span>
                            <span>
                              {dayjs(item.todayStartTime).format('mm')}
                            </span>
                            <span className='pl-2'>
                              {dayjs(item.todayStartTime).format('DD/MM')}
                            </span>
                          </div>
                          <div className='text-mainBody text-center leading-6'>
                            Sắp diễn ra
                          </div>
                        </div>
                      )}
                    </SwiperSlide>
                  ))
                : ''}
            </Swiper>
          </div>

          <div className='flash-sale-filter-cat flex gap-1 py-2 px-2 text-mainBody whitespace-nowrap overflow-x-auto scrollbar-hide'>
            <span
              className={`py-1 px-3 rounded-[60px] ${
                tabActive === ''
                  ? ' text-white bg-[#F00] font-bold '
                  : 'bg-white'
              }`}
              onClick={() => setTabActive('')}
            >
              Tất cả
            </span>
            {categoryTabs.map(item => {
              const tabKey = `${item.type}:${item.key}`
              return (
                <span
                  key={`ftab-${tabKey}`}
                  className={`font-[400] py-1 px-3 rounded-[60px] ${
                    tabActive === tabKey
                      ? ' text-white bg-[#F00] font-bold '
                      : 'bg-white'
                  }`}
                  onClick={() => setTabActive(tabKey)}
                >
                  {getTranslate(item.name)}
                </span>
              )
            })}
          </div>

          <div className='flash-sale--content min-h-[640px]'>
            {flashSale?.products && flashSale?.products?.length >= 4 ? (
              <Swiper
                modules={[Grid, FreeMode, Autoplay, Navigation]}
                className='flashSaleSwp'
                slidesPerView={2}
                grid={{
                  rows: 2,
                  fill: 'row',
                }}
                slidesPerGroup={2}
                freeMode={true}
                spaceBetween={8}
                autoplay={false}
                speed={400}
                navigation={true}
              >
                {flashSale?.products?.map((product: Product, idx: number) => (
                  <SwiperSlide key={product._id + idx + 'fs'}>
                    <ProductCard
                      product={product}
                      type={
                        flashSaleTimeActive &&
                        flashSaleTimeActive.fsStatus === 'running'
                          ? ProductCardType.FLASH_SALE
                          : ProductCardType.FLASH_SALE_TIME
                      }
                      layout={2}
                      className={`flashSaleSlider h-[336px]`}
                      showDeal
                      showRate={false}
                      showCountDown={false}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            ) : (
              <div className='max-w-screen grid grid-cols-2 gap-2 px-2'>
                {flashSale?.products &&
                  flashSale?.products.map((product: Product, idx: number) => (
                    <ProductCard
                      key={product._id + idx + 'fs'}
                      product={product}
                      type={
                        flashSaleTimeActive &&
                        flashSaleTimeActive.fsStatus === 'running'
                          ? ProductCardType.FLASH_SALE
                          : ProductCardType.FLASH_SALE_TIME
                      }
                      layout={2}
                      className={`h-[330px] `}
                      showDeal
                      showRate={false}
                      showCountDown={false}
                    />
                  ))}
              </div>
            )}
          </div>
          {flashSale?.customization &&
            flashSale?.customization?.viewMoreLink && (
              <div className='py-4'>
                <Link
                  href={flashSale?.customization?.viewMoreLink}
                  className='text-mainBody text-center font-bold text-redPico overflow-hidden h-6 leading-6 block px-3'
                  style={{
                    backgroundColor:
                      flashSale?.customization?.viewMoreButtonColor,
                    color: flashSale?.customization?.viewMoreColor,
                  }}
                >
                  {`Xem thêm DEAL >>>`}
                </Link>
              </div>
            )}
        </div>
      ) : (
        ''
      )}
    </section>
  )
}
export default FlashSale
